import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../../context/ThemeContext";

type ResponsiveWidths = {
  [breakpoint: number]: { [colIndex: number]: string };
};

type ZebraColors = { odd: string; even: string };

type FinanceStatus = "pending_payment" | "paid";
type FinanceFilter = "__all__" | "pending" | "paid";

type TableProps = {
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  columnWidths?: string[];
  responsiveWidths?: ResponsiveWidths;
  rowIds?: Array<string | number>;
  onDoubleClick?: (
    viewIndex: number,
    rowData: (string | number | React.ReactNode)[],
    originalIndex?: number
  ) => void;
  isTableStandby?: boolean;
  osColumnIndex?: number;
  enableCellColors?: boolean;
  getCellStyle?: (
    rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    originalIndex: number,
    rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => React.CSSProperties | undefined;
  enableZebra?: boolean;
  zebraColors?: ZebraColors;
  hoverHighlight?: boolean;
  dense?: boolean;
  softBorders?: boolean;
  stickyFirstCol?: boolean;
  atrasoColumnIndex?: number;
  atrasoColors?: {
    good?: string;
    warn?: string;
    bad?: string;
  };
  isRowPending?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => boolean;
  delayScope?: "cell" | "row";
  getRowGroup?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => string | null;
  groupLabels?: { [groupKey: string]: string };
  companyColumnIndex?: number;
  showFinanceFilter?: boolean;
};

type SortConfig = { index: number | null; direction: "asc" | "desc" };
type ChipKey =
  | "all"
  | "po"
  | "withpo"
  | "component"
  | "analysis"
  | "pickup"
  | "ready";

type OverlayState = {
  visible: boolean;
  x: number;
  y: number;
  text: string;
};

const normalizeText = (v: any) =>
  (typeof v === "string" || typeof v === "number" ? String(v) : "")
    .trim()
    .toLowerCase();

const normalizeStatusText = (v: any) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normalizeName = (v: any) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const includesAny = (text: string, terms: string[]) =>
  terms.some((term) => text.includes(term));

function cellToPlainText(cell: any): string {
  if (cell === null || cell === undefined) return "";

  if (typeof cell === "string" || typeof cell === "number") {
    return String(cell);
  }

  if (Array.isArray(cell)) {
    return cell.map(cellToPlainText).join(" ").trim();
  }

  if (React.isValidElement(cell)) {
    const props: any = cell.props || {};

    const ownTitle =
      typeof props.title === "string" || typeof props["aria-label"] === "string"
        ? String(props.title || props["aria-label"])
        : "";

    const childrenText = cellToPlainText(props.children);

    return [ownTitle, childrenText].filter(Boolean).join(" ").trim();
  }

  if (typeof cell === "object") {
    try {
      return JSON.stringify(cell);
    } catch {
      return "";
    }
  }

  return "";
}

function splitNamesForOverlay(text: string): string[] {
  const raw = String(text || "").trim();
  if (!raw) return [];

  if (
    raw === "—" ||
    raw === "-" ||
    raw.toLowerCase() === "n/a" ||
    raw.toLowerCase() === "na"
  ) {
    return [];
  }

  return raw
    .split(/\s*,\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
}

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  columnWidths = [],
  responsiveWidths = {},
  rowIds,
  onDoubleClick,
  isTableStandby = false,
  osColumnIndex = 0,
  enableCellColors = false,
  getCellStyle,
  enableZebra = true,
  zebraColors = { odd: "#f6f8fb", even: "#fbfcfe" },
  hoverHighlight = true,
  dense = false,
  softBorders = false,
  stickyFirstCol = false,
  atrasoColumnIndex,
  atrasoColors,
  isRowPending,
  delayScope = "cell",
  getRowGroup,
  groupLabels,
  companyColumnIndex,
  showFinanceFilter = false,
}) => {
  const [filters, setFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );

  const [statusChip, setStatusChip] = useState<ChipKey>("all");
  const [companyFilter, setCompanyFilter] = useState<string>("__all__");
  const [personFilter, setPersonFilter] = useState<string>("__all__");
  const [financeFilter, setFinanceFilter] = useState<FinanceFilter>("__all__");

  const [financeMap, setFinanceMap] = useState<Record<number, FinanceStatus>>(
    {}
  );

  const [selectedViewIndex, setSelectedViewIndex] = useState<number | null>(
    null
  );
  const [selectedOS, setSelectedOS] = useState<string | null>(null);
  const [activeWidths, setActiveWidths] = useState<string[]>(columnWidths);
  const [fontSize, setFontSize] = useState("14px");
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [sort, setSort] = useState<SortConfig>({
    index: null,
    direction: "asc",
  });

  const [overlay, setOverlay] = useState<OverlayState>({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });

  const overlayRafRef = useRef<number | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();
  const headerPrimary = theme?.primary ?? "#1f290c";

  const SELECTED_SOFT = "#EEF1F5";
  const optionHover = theme?.table_option_hover ?? "#E9EEF6";
  const borderColor = softBorders ? "#e5e7eb" : "black";

  const PENDING_BASE = "#FFF9D6";
  const PENDING_SELECTED_SOLID = "#F3E1A5B8";

  const atrasoGood = atrasoColors?.good ?? "#DCFCE7";
  const atrasoWarn = atrasoColors?.warn ?? "#F0BF8A";
  const atrasoBad = atrasoColors?.bad ?? "#C44949";

  const FINANCE_COLORS: Record<FinanceStatus, string> = {
    pending_payment: "#F50B0B",
    paid: "#22C55E",
  };

  useEffect(() => {
    const nextFinance: Record<number, FinanceStatus> = {};
    rows.forEach((_, i) => {
      nextFinance[i] = "pending_payment";
    });
    setFinanceMap(nextFinance);
  }, [rows]);

  useEffect(() => {
    setFilters(Array(headers.length).fill(""));
  }, [headers.length]);

  const statusColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => normalizeText(h).includes("status"));
    return idx >= 0 ? idx : null;
  }, [headers]);

  const copiaColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => normalizeText(h) === "em cópia");
    return idx >= 0 ? idx : null;
  }, [headers]);

  const solicitanteColIndex = useMemo(() => {
    const idx = headers.findIndex((h) => {
      const n = normalizeText(h);
      return (
        n.includes("solicitante") ||
        n.includes("responsável") ||
        n.includes("responsavel")
      );
    });
    return idx >= 0 ? idx : null;
  }, [headers]);

  const effectiveCompanyColIndex = useMemo(() => {
    if (
      typeof companyColumnIndex === "number" &&
      companyColumnIndex >= 0 &&
      companyColumnIndex < headers.length
    ) {
      return companyColumnIndex;
    }

    const idx = headers.findIndex((h) => normalizeText(h).includes("empresa"));
    return idx >= 0 ? idx : null;
  }, [companyColumnIndex, headers]);

  const firstVisibleColIndex = useMemo(() => {
    const idx = activeWidths.findIndex((w) => w !== "0px");
    return idx >= 0 ? idx : 0;
  }, [activeWidths]);

  const getStatusText = (cell: any) => cellToPlainText(cell);

  const statusKeyOf = (statusText: string): ChipKey | "other" => {
    const s = normalizeStatusText(statusText);

    if (
      includesAny(s, [
        "equipamento com p.o.",
        "equipamento com p.o",
        "equipamento com po",
        "com p.o.",
        "com p.o",
        "com po",
      ])
    ) {
      return "withpo";
    }

    if (
      includesAny(s, [
        "aguardando p.o.",
        "aguardando p.o",
        "aguardando po",
        "equipamento aguardando p.o.",
        "equipamento aguardando p.o",
        "equipamento aguardando po",
      ])
    ) {
      return "po";
    }

    if (
      includesAny(s, [
        "aguardando componente",
        "equipamento aguardando componente",
      ])
    ) {
      return "component";
    }

    if (
      includesAny(s, [
        "em analise",
        "em análise",
        "equipamento em analise",
        "equipamento em análise",
      ])
    ) {
      return "analysis";
    }

    if (
      includesAny(s, [
        "aguardando retirada",
        "aguardando retirada do equipamento",
      ])
    ) {
      return "pickup";
    }

    if (
      includesAny(s, [
        "equipamento reparado",
        "equipamento operacional",
        "reparado",
        "operacional",
      ])
    ) {
      return "ready";
    }

    return "other";
  };

  const statusStyle = (statusText: string) => {
    const key = statusKeyOf(statusText);

    if (key === "po") {
      return { bg: "#FDE7C7", fg: "#000000" };
    }
    if (key === "withpo") {
      return { bg: "#DCFCE7", fg: "#000000" };
    }
    if (key === "component") {
      return { bg: "#FEF3C7", fg: "#000000" };
    }
    if (key === "analysis") {
      return { bg: "#DBEAFE", fg: "#000000" };
    }
    if (key === "pickup") {
      return { bg: "#FEE2E2", fg: "#000000" };
    }
    if (key === "ready") {
      return { bg: "#D1FAE5", fg: "#000000" };
    }

    return { bg: "#F3F4F6", fg: "#000000" };
  };

  const statusMatchesChip = useCallback(
    (statusText: string) => {
      if (statusChip === "all") return true;
      return statusKeyOf(statusText) === statusChip;
    },
    [statusChip]
  );

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      const sorted = Object.keys(responsiveWidths)
        .map(Number)
        .sort((a, b) => b - a);
      const bp = sorted.find((x) => width <= x) ?? null;

      if (bp) {
        const conf = responsiveWidths[bp];
        const w = headers.map(
          (_, i) => conf[i + 1] || columnWidths[i] || "auto"
        );
        setActiveWidths(w);
      } else {
        setActiveWidths(columnWidths);
      }

      const clamped = Math.max(12, Math.min(18, width / 120));
      setFontSize(clamped + "px");
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [responsiveWidths, headers, columnWidths]);

  useEffect(() => {
    const hide = () =>
      setOverlay((prev) => (prev.visible ? { ...prev, visible: false } : prev));

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      if (overlayRafRef.current != null) {
        cancelAnimationFrame(overlayRafRef.current);
      }
    };
  }, []);

  const parseMaybeNumber = (v: string): number | null => {
    const n = Number(
      v
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".")
    );
    return Number.isFinite(n) ? n : null;
  };

  const extractOS = (cell: string | number | React.ReactNode): string => {
    if (typeof cell === "string" || typeof cell === "number")
      return String(cell);

    if (
      cell &&
      typeof cell === "object" &&
      "props" in (cell as any) &&
      (cell as any).props?.os
    ) {
      return String((cell as any).props.os);
    }

    return cellToPlainText(cell);
  };

  const getComparable = (cell: string | number | React.ReactNode) => {
    const asText = cellToPlainText(cell);

    if (typeof cell === "number") return { kind: "num" as const, val: cell };

    if (typeof cell === "string") {
      const maybe = parseMaybeNumber(cell);
      if (maybe !== null) return { kind: "num" as const, val: maybe };
      return { kind: "str" as const, val: cell };
    }

    const maybe = parseMaybeNumber(asText);
    if (maybe !== null) return { kind: "num" as const, val: maybe };

    return { kind: "str" as const, val: asText };
  };

  const rowBaseBg = (i: number) =>
    enableZebra
      ? i % 2 === 0
        ? zebraColors.odd
        : zebraColors.even
      : "#ffffff";

  const chipCounts = useMemo(() => {
    const counts: Record<ChipKey, number> = {
      all: rows.length,
      po: 0,
      withpo: 0,
      component: 0,
      analysis: 0,
      pickup: 0,
      ready: 0,
    };

    if (statusColIndex == null) return counts;

    for (const r of rows) {
      const txt = getStatusText(r[statusColIndex]);
      const key = statusKeyOf(txt);
      if (key !== "other") counts[key] += 1;
    }

    return counts;
  }, [rows, statusColIndex]);

  const companyOptions = useMemo(() => {
    if (effectiveCompanyColIndex == null) return [];

    const map = new Map<string, { label: string; count: number }>();

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      }

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (
          (financeFilter === "pending" && financeState !== "pending_payment") ||
          (financeFilter === "paid" && financeState !== "paid")
        ) {
          return;
        }
      }

      const company = cellToPlainText(row[effectiveCompanyColIndex]).trim();
      if (!company) return;

      const curr = map.get(company);
      if (curr) curr.count += 1;
      else map.set(company, { label: company, count: 1 });
    });

    return Array.from(map.entries())
      .map(([value, meta]) => ({
        value,
        label: meta.label,
        count: meta.count,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [
    effectiveCompanyColIndex,
    rows,
    statusColIndex,
    statusMatchesChip,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const personOptions = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      }

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (
          (financeFilter === "pending" && financeState !== "pending_payment") ||
          (financeFilter === "paid" && financeState !== "paid")
        ) {
          return;
        }
      }

      if (companyFilter !== "__all__" && effectiveCompanyColIndex != null) {
        const companyTxt = cellToPlainText(
          row[effectiveCompanyColIndex]
        ).trim();
        if (companyTxt !== companyFilter) return;
      }

      const names = new Set<string>();

      if (solicitanteColIndex != null) {
        const solicitante = cellToPlainText(row[solicitanteColIndex]).trim();
        if (solicitante) names.add(solicitante);
      }

      if (copiaColIndex != null) {
        splitNamesForOverlay(cellToPlainText(row[copiaColIndex])).forEach(
          (n) => {
            if (n) names.add(n);
          }
        );
      }

      Array.from(names).forEach((name) => {
        const existing = map.get(name);
        if (existing) existing.count += 1;
        else map.set(name, { label: name, count: 1 });
      });
    });

    return Array.from(map.entries())
      .map(([value, meta]) => ({
        value,
        label: meta.label,
        count: meta.count,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
      );
  }, [
    rows,
    statusColIndex,
    statusMatchesChip,
    companyFilter,
    effectiveCompanyColIndex,
    solicitanteColIndex,
    copiaColIndex,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const rowMatchesPersonFilter = useCallback(
    (row: (string | number | React.ReactNode)[]) => {
      if (personFilter === "__all__") return true;

      const target = normalizeName(personFilter);
      if (!target) return true;

      let solicitanteMatch = false;
      let copiaMatch = false;

      if (solicitanteColIndex != null) {
        const solicitante = normalizeName(
          cellToPlainText(row[solicitanteColIndex])
        );
        solicitanteMatch = solicitante === target;
      }

      if (copiaColIndex != null) {
        const copiaNames = splitNamesForOverlay(
          cellToPlainText(row[copiaColIndex])
        ).map((n) => normalizeName(n));
        copiaMatch = copiaNames.includes(target);
      }

      return solicitanteMatch || copiaMatch;
    },
    [personFilter, solicitanteColIndex, copiaColIndex]
  );

  const { visibleRows, viewToOriginal } = useMemo(() => {
    const filtered: {
      row: (string | number | React.ReactNode)[];
      original: number;
    }[] = [];

    rows.forEach((row, originalIdx) => {
      if (statusColIndex != null) {
        const statusTxt = getStatusText(row[statusColIndex]);
        if (!statusMatchesChip(statusTxt)) return;
      } else {
        if (statusChip !== "all") return;
      }

      if (effectiveCompanyColIndex != null && companyFilter !== "__all__") {
        const companyTxt = cellToPlainText(
          row[effectiveCompanyColIndex]
        ).trim();
        if (companyTxt !== companyFilter) return;
      }

      if (!rowMatchesPersonFilter(row)) return;

      if (showFinanceFilter) {
        const financeState = financeMap[originalIdx] ?? "pending_payment";
        if (financeFilter === "pending" && financeState !== "pending_payment")
          return;
        if (financeFilter === "paid" && financeState !== "paid") return;
      }

      const passes = row.every((cell, i) => {
        const f = (filters[i] ?? "").toLowerCase().trim();
        if (!f) return true;

        const v = cellToPlainText(cell).toLowerCase();
        return v.includes(f);
      });

      if (passes) filtered.push({ row, original: originalIdx });
    });

    const statusPriority: Record<string, number> = {
      po: 0,
      withpo: 1,
      component: 2,
      analysis: 3,
      pickup: 4,
      ready: 5,
      other: 9,
    };

    const priorityOfRow = (r: (string | number | React.ReactNode)[]) => {
      if (statusColIndex == null) return statusPriority.other;
      const txt = getStatusText(r[statusColIndex]);
      const key = statusKeyOf(txt);
      return statusPriority[key] ?? statusPriority.other;
    };

    const doSortByColumn = (a: any, b: any) => {
      if (sort.index == null) return 0;
      const i = sort.index;
      const m = sort.direction === "asc" ? 1 : -1;

      const A = getComparable(a.row[i]);
      const B = getComparable(b.row[i]);

      if (A.kind === "num" && B.kind === "num") {
        if (A.val < B.val) return -1 * m;
        if (A.val > B.val) return 1 * m;
        return 0;
      }

      return (
        String(A.val).localeCompare(String(B.val), "pt-BR", {
          sensitivity: "base",
          numeric: true,
        }) * m
      );
    };

    filtered.sort((a, b) => {
      const ap = priorityOfRow(a.row);
      const bp = priorityOfRow(b.row);
      if (ap !== bp) return ap - bp;

      const byCol = doSortByColumn(a, b);
      if (byCol !== 0) return byCol;

      return a.original - b.original;
    });

    return {
      visibleRows: filtered.map((x) => x.row),
      viewToOriginal: filtered.map((x) => x.original),
    };
  }, [
    rows,
    filters,
    sort,
    statusColIndex,
    statusChip,
    statusMatchesChip,
    effectiveCompanyColIndex,
    companyFilter,
    rowMatchesPersonFilter,
    financeMap,
    financeFilter,
    showFinanceFilter,
  ]);

  const selectRow = useCallback(
    (index: number | null) => {
      if (index == null || index < 0 || index >= visibleRows.length) return;

      const linha = visibleRows[index];

      if (isTableStandby) {
        const raw = linha[osColumnIndex];
        const osValue = extractOS(raw);
        setSelectedOS(osValue || null);
        setSelectedViewIndex(null);
      } else {
        setSelectedViewIndex(index);
        setSelectedOS(null);
      }

      const root = tableRef.current;
      if (root) {
        const trs = root.querySelectorAll("tbody tr");
        if (trs[index])
          (trs[index] as HTMLElement).scrollIntoView({ block: "nearest" });
      }
    },
    [visibleRows, isTableStandby, osColumnIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      if (!visibleRows.length) return;

      let current =
        isTableStandby && selectedOS
          ? visibleRows.findIndex(
              (r) => extractOS(r[osColumnIndex]) === selectedOS
            )
          : selectedViewIndex ?? -1;

      if (e.key === "ArrowDown")
        current = Math.min(visibleRows.length - 1, current + 1);
      else current = Math.max(0, current - 1);

      selectRow(current);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visibleRows,
    selectedOS,
    selectedViewIndex,
    selectRow,
    isTableStandby,
    osColumnIndex,
  ]);

  const colorForDelay = (value: string | number | React.ReactNode) => {
    const txt = cellToPlainText(value);

    let n: number | null = null;
    if (typeof value === "number") n = Number.isFinite(value) ? value : null;
    else if (typeof value === "string") n = parseMaybeNumber(value);
    else n = parseMaybeNumber(txt);

    if (n == null) return null;

    let bg = atrasoBad;
    if (n <= 3) bg = atrasoGood;
    else if (n <= 9) bg = atrasoWarn;

    const fg = bg === atrasoBad ? "#fff" : "#111";
    return { bg, fg, n };
  };

  const looksLikeYellow = (c?: string) => {
    if (!c) return false;
    const x = c.toLowerCase().replace(/\s+/g, "");
    return (
      x.includes("#fff9d6") ||
      x.includes("#fffac8") ||
      x.includes("ffe79a") ||
      x.includes("fff7ed") ||
      x.includes("fffbeb")
    );
  };

  const isPendingRow = (
    linha: (string | number | React.ReactNode)[],
    vIndex: number,
    oIndex: number
  ) => {
    if (isRowPending) return !!isRowPending(linha, vIndex, oIndex);
    if ((linha as any)?.__pendente_retorno === true) return true;

    if (enableCellColors && getCellStyle) {
      for (let col = 0; col < headers.length; col++) {
        const extra =
          getCellStyle(vIndex, col, linha[col], oIndex, linha, headers[col]) ||
          {};
        const bg =
          (extra.backgroundColor as string) || (extra.background as string);
        if (looksLikeYellow(bg)) return true;
      }
    }

    return false;
  };

  const moveOverlay = useCallback(
    (clientX: number, clientY: number, text: string) => {
      if (overlayRafRef.current != null) {
        cancelAnimationFrame(overlayRafRef.current);
      }

      overlayRafRef.current = requestAnimationFrame(() => {
        setOverlay({
          visible: true,
          x: clientX + 14,
          y: clientY + 18,
          text,
        });
      });
    },
    []
  );

  const hideOverlay = useCallback(() => {
    if (overlayRafRef.current != null) {
      cancelAnimationFrame(overlayRafRef.current);
      overlayRafRef.current = null;
    }

    setOverlay((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const headerHeight = dense ? "36px" : "40px";
  const filterHeight = dense ? "30px" : "35px";
  const cellHeight = dense ? "32px" : "35px";
  const cellPadding = dense ? "0 4px" : "0 5px";

  const activeColSpan = useMemo(
    () =>
      headers.reduce(
        (acc, _h, i) => (activeWidths[i] === "0px" ? acc : acc + 1),
        0
      ),
    [headers, activeWidths]
  );

  const getStableRowKey = (viewIndex: number, originalIndex: number) => {
    const rid =
      rowIds && typeof originalIndex === "number"
        ? rowIds[originalIndex]
        : null;

    if (rid !== null && rid !== undefined && String(rid).trim() !== "") {
      return `row-${String(rid)}`;
    }

    return `row-${originalIndex}-${viewIndex}`;
  };

  const chipWrapStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "10px 0 12px",
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    height: 32,
    padding: "0 12px",
    borderRadius: 999,
    border: active ? `1px solid ${headerPrimary}` : "1px solid #cbd5e1",
    background: active ? headerPrimary : "#ffffff",
    color: active ? "#fff" : "#111827",
    fontSize: "12px",
    fontWeight: 400,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    userSelect: "none",
    transition: "all 0.15s ease",
    boxShadow: active ? "0 6px 16px rgba(0,0,0,0.12)" : "none",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  });

  const chipDot = (bg: string): React.CSSProperties => ({
    width: 10,
    height: 10,
    borderRadius: 999,
    background: bg,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.25) inset",
    flexShrink: 0,
  });

  const chipCountPill: React.CSSProperties = {
    marginLeft: 2,
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.22)",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    flexShrink: 0,
  };

  const filterBarStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    padding: "0 0 12px",
  };

  const filterBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 260,
    flex: "0 1 320px",
  };

  const filterLabelStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#374151",
    fontWeight: 500,
  };

  const selectStyle: React.CSSProperties = {
    height: 36,
    borderRadius: 0,
    border: "1px solid #cbd5e1",
    padding: "0 10px",
    fontSize: 14,
    outline: "none",
    background: "#ffffff",
    color: "#111827",
  };

  const ChipLabel: React.FC<{ label: string; count: number }> = ({
    label,
    count,
  }) => (
    <>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-block",
          maxWidth: "100%",
        }}
        title={label}
      >
        {label}
      </span>
      <span style={chipCountPill}>{count}</span>
    </>
  );

  const overlayNode =
    typeof document !== "undefined" && overlay.visible
      ? createPortal(
          <div
            style={{
              position: "fixed",
              left: overlay.x,
              top: overlay.y,
              zIndex: 999999,
              pointerEvents: "none",
              maxWidth: 360,
              background: "rgba(17, 24, 39, 0.96)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              padding: "8px 10px",
              boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
              fontSize: 12,
              lineHeight: 1.35,
              whiteSpace: "normal",
            }}
          >
            {splitNamesForOverlay(overlay.text).length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {splitNamesForOverlay(overlay.text).map((name, idx) => (
                  <div key={`${name}-${idx}`}>{name}</div>
                ))}
              </div>
            ) : (
              <div>{overlay.text}</div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div ref={tableRef} style={{ width: "100%", overflowX: "auto" }}>
        <div style={chipWrapStyle}>
          <button
            type="button"
            style={chipStyle(statusChip === "all")}
            onClick={() => {
              setStatusChip("all");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <ChipLabel label="Todos" count={chipCounts.all} />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "po")}
            onClick={() => {
              setStatusChip("po");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#F59E0B")} />
            <ChipLabel label="Aguardando P.O." count={chipCounts.po} />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "withpo")}
            onClick={() => {
              setStatusChip("withpo");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#22C55E")} />
            <ChipLabel label="Equipamento com P.O." count={chipCounts.withpo} />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "component")}
            onClick={() => {
              setStatusChip("component");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#EAB308")} />
            <ChipLabel
              label="Aguardando componente"
              count={chipCounts.component}
            />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "analysis")}
            onClick={() => {
              setStatusChip("analysis");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#2564EB")} />
            <ChipLabel label="Em análise" count={chipCounts.analysis} />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "pickup")}
            onClick={() => {
              setStatusChip("pickup");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#B21515")} />
            <ChipLabel label="Aguardando retirada" count={chipCounts.pickup} />
          </button>

          <button
            type="button"
            style={chipStyle(statusChip === "ready")}
            onClick={() => {
              setStatusChip("ready");
              setSelectedOS(null);
              setSelectedViewIndex(null);
            }}
          >
            <span style={chipDot("#16A34A")} />
            <ChipLabel label="Reparado/Operacional" count={chipCounts.ready} />
          </button>
        </div>

        <div style={filterBarStyle}>
          {showFinanceFilter && (
            <div style={filterBlockStyle}>
              <label style={filterLabelStyle}>Financeiro</label>
              <select
                value={financeFilter}
                onChange={(e) => {
                  setFinanceFilter(e.target.value as FinanceFilter);
                  setSelectedOS(null);
                  setSelectedViewIndex(null);
                  hideOverlay();
                }}
                style={selectStyle}
              >
                <option value="__all__">Todos</option>
                <option value="pending">Aguardando pagamento</option>
                <option value="paid">Pagos</option>
              </select>
            </div>
          )}

          {effectiveCompanyColIndex != null && (
            <div style={filterBlockStyle}>
              <label style={filterLabelStyle}>Empresa</label>
              <select
                value={companyFilter}
                onChange={(e) => {
                  setCompanyFilter(e.target.value);
                  setPersonFilter("__all__");
                  setSelectedOS(null);
                  setSelectedViewIndex(null);
                  hideOverlay();
                }}
                style={selectStyle}
              >
                <option value="__all__">Todas as empresas</option>
                {companyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} ({opt.count})
                  </option>
                ))}
              </select>
            </div>
          )}

          {(solicitanteColIndex != null || copiaColIndex != null) && (
            <div style={filterBlockStyle}>
              <label style={filterLabelStyle}>Solicitante / Em cópia</label>
              <select
                value={personFilter}
                onChange={(e) => {
                  setPersonFilter(e.target.value);
                  setSelectedOS(null);
                  setSelectedViewIndex(null);
                  hideOverlay();
                }}
                style={selectStyle}
              >
                <option value="__all__">Todas as pessoas</option>
                {personOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} ({opt.count})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) =>
                activeWidths[index] !== "0px" ? (
                  <th
                    key={index}
                    onClick={() =>
                      setSort((prev) =>
                        prev.index === index
                          ? {
                              index,
                              direction:
                                prev.direction === "asc" ? "desc" : "asc",
                            }
                          : { index, direction: "asc" }
                      )
                    }
                    style={{
                      width: activeWidths[index],
                      backgroundColor: headerPrimary,
                      color: "#ffffff",
                      fontWeight: 300,
                      fontSize,
                      height: headerHeight,
                      padding: "0 8px",
                      borderTop: `1px solid ${borderColor}`,
                      borderRight: `1px solid ${borderColor}`,
                      borderBottom: `1px solid ${borderColor}`,
                      borderLeft:
                        index === firstVisibleColIndex
                          ? `4px solid ${borderColor}`
                          : `1px solid ${borderColor}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      textAlign: "center",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    role="button"
                    aria-sort={
                      sort.index === index
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    title="Clique para ordenar"
                  >
                    <div
                      style={{
                        minHeight: headerHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {header}
                    </div>
                  </th>
                ) : null
              )}
            </tr>
          </thead>

          <tbody>
            <tr>
              {headers.map((_, index) =>
                activeWidths[index] !== "0px" ? (
                  <td
                    key={`filter-${index}`}
                    style={{
                      width: activeWidths[index],
                      minHeight: filterHeight,
                      height: filterHeight,
                      borderTop: `1px solid ${borderColor}`,
                      borderRight: `1px solid ${borderColor}`,
                      borderBottom: `1px solid ${borderColor}`,
                      borderLeft:
                        index === firstVisibleColIndex
                          ? `4px solid ${borderColor}`
                          : `1px solid ${borderColor}`,
                      padding: "0 8px",
                      background: "#f8fafc",
                      verticalAlign: "middle",
                      position:
                        index === 0 && stickyFirstCol ? "sticky" : undefined,
                      left: index === 0 && stickyFirstCol ? 0 : undefined,
                      zIndex: index === 0 && stickyFirstCol ? 1 : undefined,
                    }}
                  >
                    <div
                      style={{
                        minHeight: filterHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <input
                        type="text"
                        value={filters[index]}
                        onChange={(e) => {
                          const nf = [...filters];
                          nf[index] = e.target.value;
                          setFilters(nf);
                          setSelectedOS(null);
                          setSelectedViewIndex(null);
                          hideOverlay();
                        }}
                        style={{
                          width: "100%",
                          height: dense ? "24px" : "28px",
                          border: "none",
                          outline: "none",
                          fontSize,
                          textAlign: "center",
                          textTransform: "uppercase",
                          background: "transparent",
                        }}
                      />
                    </div>
                  </td>
                ) : null
              )}
            </tr>

            {visibleRows.length > 0 &&
              (() => {
                const result: React.ReactNode[] = [];
                let lastGroupKey: string | null = null;

                visibleRows.forEach((linha, viewIndex) => {
                  const originalIndex = viewToOriginal[viewIndex];
                  const stableKey = getStableRowKey(viewIndex, originalIndex);

                  const osValue = isTableStandby
                    ? extractOS(linha[osColumnIndex]) || null
                    : null;

                  const pendente = isPendingRow(
                    linha,
                    viewIndex,
                    originalIndex
                  );

                  const isSelected = isTableStandby
                    ? selectedOS != null && osValue === selectedOS
                    : selectedViewIndex === viewIndex;

                  const base = pendente ? PENDING_BASE : rowBaseBg(viewIndex);
                  const baseOrHover =
                    hoverRow === viewIndex && !isSelected ? optionHover : base;

                  const rowBaseLayer = `linear-gradient(0deg, ${baseOrHover}, ${baseOrHover})`;
                  const selectionLayer = isSelected
                    ? `linear-gradient(0deg, ${
                        pendente ? PENDING_SELECTED_SOLID : SELECTED_SOFT
                      }, ${pendente ? PENDING_SELECTED_SOLID : SELECTED_SOFT})`
                    : "";

                  const rowLayers: string[] = [];
                  if (selectionLayer) rowLayers.push(selectionLayer);
                  rowLayers.push(rowBaseLayer);

                  const groupKey =
                    getRowGroup?.(linha, viewIndex, originalIndex) || null;
                  const showGroupBar =
                    groupKey &&
                    groupLabels &&
                    groupLabels[groupKey] &&
                    groupKey !== lastGroupKey;

                  const financeState =
                    financeMap[originalIndex] ?? "pending_payment";
                  const financeBorderColor = showFinanceFilter
                    ? FINANCE_COLORS[financeState]
                    : borderColor;

                  if (showGroupBar) {
                    lastGroupKey = groupKey;
                    result.push(
                      <tr key={`group-${groupKey}-${stableKey}`}>
                        <td
                          colSpan={activeColSpan || headers.length}
                          style={{
                            padding: "6px 10px",
                            background: "#264a7d",
                            color: "#ffffff",
                            fontWeight: 300,
                            fontSize,
                            textAlign: "left",
                            verticalAlign: "middle",
                            letterSpacing: "0.03em",
                            borderTop: "1px solid black",
                            borderBottom: "1px solid black",
                            borderLeft: "1px solid black",
                            borderRight: "1px solid black",
                          }}
                        >
                          {groupLabels![groupKey]}
                        </td>
                      </tr>
                    );
                  }

                  result.push(
                    <tr
                      key={stableKey}
                      onClick={() => selectRow(viewIndex)}
                      onDoubleClick={() =>
                        onDoubleClick?.(viewIndex, linha, originalIndex)
                      }
                      onMouseEnter={() =>
                        hoverHighlight && setHoverRow(viewIndex)
                      }
                      onMouseLeave={() => {
                        hoverHighlight && setHoverRow(null);
                        hideOverlay();
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundImage: rowLayers.join(", "),
                        backgroundOrigin: "padding-box",
                        backgroundClip: "padding-box",
                      }}
                    >
                      {headers.map((headerTitle, colIndex) => {
                        if (activeWidths[colIndex] === "0px") return null;

                        const extra =
                          enableCellColors && getCellStyle
                            ? getCellStyle(
                                viewIndex,
                                colIndex,
                                linha[colIndex],
                                originalIndex,
                                linha,
                                headerTitle
                              ) || {}
                            : {};

                        let extraBg: string | null = null;
                        if (extra.backgroundColor)
                          extraBg = String(extra.backgroundColor);
                        else if (extra.background)
                          extraBg = String(extra.background);

                        const extraLayer = extraBg
                          ? `linear-gradient(0deg, ${extraBg}, ${extraBg})`
                          : "";

                        const isDelayCell =
                          typeof atrasoColumnIndex === "number" &&
                          colIndex === atrasoColumnIndex;

                        const delayInfo = isDelayCell
                          ? colorForDelay(linha[colIndex])
                          : null;

                        const delayLayerRow =
                          delayScope === "row" &&
                          typeof atrasoColumnIndex === "number"
                            ? (() => {
                                const val =
                                  linha[atrasoColumnIndex as number] ??
                                  undefined;
                                const info = colorForDelay(val);
                                return info
                                  ? `linear-gradient(0deg, ${info.bg}, ${info.bg})`
                                  : "";
                              })()
                            : "";

                        const statusLayer =
                          statusColIndex != null && colIndex === statusColIndex
                            ? (() => {
                                const txt = cellToPlainText(linha[colIndex]);
                                const st = statusStyle(txt);
                                return st
                                  ? `linear-gradient(0deg, ${st.bg}, ${st.bg})`
                                  : "";
                              })()
                            : "";

                        const statusFg =
                          statusColIndex != null && colIndex === statusColIndex
                            ? (() => {
                                const txt = cellToPlainText(linha[colIndex]);
                                const st = statusStyle(txt);
                                return st?.fg;
                              })()
                            : undefined;

                        const isCopiaCell =
                          copiaColIndex != null && colIndex === copiaColIndex;

                        const overlayText = isCopiaCell
                          ? cellToPlainText(linha[colIndex])
                          : "";

                        const hasOverlayContent =
                          isCopiaCell &&
                          splitNamesForOverlay(overlayText).length > 0;

                        const layersCell: string[] = [];
                        if (delayInfo && delayScope === "cell") {
                          layersCell.push(
                            `linear-gradient(0deg, ${delayInfo.bg}, ${delayInfo.bg})`
                          );
                        }
                        if (statusLayer) layersCell.push(statusLayer);
                        if (extraLayer) layersCell.push(extraLayer);
                        if (delayLayerRow) layersCell.push(delayLayerRow);
                        layersCell.push(...rowLayers);

                        const fgColor =
                          delayInfo && delayScope === "cell"
                            ? delayInfo.fg
                            : statusFg ?? undefined;

                        const isFirstVisibleCell =
                          colIndex === firstVisibleColIndex;

                        return (
                          <td
                            key={`${stableKey}-c${colIndex}`}
                            onMouseEnter={(e) => {
                              if (hasOverlayContent) {
                                moveOverlay(e.clientX, e.clientY, overlayText);
                              }
                            }}
                            onMouseMove={(e) => {
                              if (hasOverlayContent) {
                                moveOverlay(e.clientX, e.clientY, overlayText);
                              }
                            }}
                            onMouseLeave={() => {
                              if (isCopiaCell) hideOverlay();
                            }}
                            style={{
                              width: activeWidths[colIndex],
                              minHeight: cellHeight,
                              height: cellHeight,
                              borderTop: `1px solid ${borderColor}`,
                              borderRight: `1px solid ${borderColor}`,
                              borderBottom: `1px solid ${borderColor}`,
                              borderLeft: isFirstVisibleCell
                                ? `4px solid ${financeBorderColor}`
                                : `1px solid ${borderColor}`,
                              fontSize,
                              fontWeight: 300,
                              textAlign: "center",
                              verticalAlign: "middle",
                              whiteSpace: isCopiaCell ? "nowrap" : "normal",
                              wordBreak: isCopiaCell ? "normal" : "break-word",
                              padding: cellPadding,
                              position:
                                stickyFirstCol && colIndex === 0
                                  ? "sticky"
                                  : undefined,
                              left:
                                stickyFirstCol && colIndex === 0
                                  ? 0
                                  : undefined,
                              zIndex:
                                stickyFirstCol && colIndex === 0
                                  ? 1
                                  : undefined,
                              backgroundImage: layersCell.join(", "),
                              backgroundOrigin: "padding-box",
                              backgroundClip: "padding-box",
                              color: fgColor,
                              overflow: isCopiaCell ? "visible" : undefined,
                              ...(() => {
                                const clone = { ...extra };
                                delete (clone as any).background;
                                delete (clone as any).backgroundColor;
                                return clone;
                              })(),
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                minHeight: cellHeight,
                                height: "100%",
                                overflow: "visible",
                                textAlign: "center",
                              }}
                            >
                              {linha[colIndex]}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                });

                return result;
              })()}

            {!visibleRows.length && (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    verticalAlign: "middle",
                    color: "#6B7280",
                    fontSize,
                    border: `1px solid ${borderColor}`,
                    background: "#fff",
                  }}
                >
                  Nenhum registro encontrado com os filtros atuais.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {overlayNode}
    </>
  );
};

export default Table;
